import { useState, useEffect, useCallback } from 'react';
import { getBookings, createBooking, updateBooking, cancelBooking, getGuests, getRooms, adminDeleteBooking, adminCleanupOrphans, adminFullReset } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { BookingFormDialog } from '../components/bookings/BookingFormDialog';
import { BulkUploadDialog } from '../components/bookings/BulkUploadDialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Plus, Calendar as CalendarIcon, Search, Filter, X, Pencil, Trash2, Sparkles, Upload, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const bookingStatuses = ['confirmed', 'checked_in', 'checked_out', 'cancelled'];

export default function Bookings() {
  const { isAdmin } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [guests, setGuests] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [allRooms, setAllRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [formData, setFormData] = useState({
    guest_id: '', room_id: '', num_guests: 1, special_requests: '', coupon_code: '',
    meal_plan: 'room_only', custom_rate: ''
  });
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [bookingsRes, guestsRes, roomsRes, allRoomsRes] = await Promise.all([
        getBookings(filterStatus || undefined),
        getGuests(),
        getRooms(),       // all rooms — date-range conflicts are enforced server-side on submit
        getRooms(),
      ]);
      setBookings(bookingsRes.data);
      setGuests(guestsRes.data);
      setRooms(roomsRes.data);
      setAllRooms(allRoomsRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { loadData(); }, [loadData]);

  const resetForm = () => {
    setFormData({ guest_id: '', room_id: '', num_guests: 1, special_requests: '', coupon_code: '', meal_plan: 'room_only', custom_rate: '' });
    setCheckInDate(null);
    setCheckOutDate(null);
    setEditingBooking(null);
  };

  const openAddModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (booking) => {
    setEditingBooking(booking);
    setFormData({
      guest_id: booking.guest_id,
      room_id: booking.room_id,
      num_guests: booking.num_guests,
      special_requests: booking.special_requests || '',
      coupon_code: booking.coupon_code || '',
      meal_plan: booking.meal_plan || 'room_only',
      custom_rate: booking.custom_rate ?? '',
    });
    setCheckInDate(parseISO(booking.check_in_date));
    setCheckOutDate(parseISO(booking.check_out_date));
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!checkInDate || !checkOutDate) {
      toast.error('Please select check-in and check-out dates');
      return;
    }

    try {
      const data = {
        ...formData,
        check_in_date: format(checkInDate, 'yyyy-MM-dd'),
        check_out_date: format(checkOutDate, 'yyyy-MM-dd'),
        num_guests: parseInt(formData.num_guests),
        coupon_code: formData.coupon_code || undefined,
        meal_plan: formData.meal_plan || 'room_only',
        custom_rate: formData.custom_rate !== '' && formData.custom_rate != null
          ? parseFloat(formData.custom_rate)
          : undefined,
      };

      if (editingBooking) {
        // Update existing booking
        const res = await updateBooking(editingBooking.id, data);
        const b = res.data;
        toast.success(`Booking ${b.booking_number || editingBooking.booking_number} updated! Total: ₹${b.total_amount}`);
      } else {
        // Create new booking
        const res = await createBooking(data);
        const b = res.data;
        const msg = b.discount > 0
          ? `Booking ${b.booking_number} created! ₹${b.base_amount} - ₹${b.discount} discount + ₹${b.gst_amount} GST = ₹${b.total_amount}`
          : `Booking ${b.booking_number} created! Total: ₹${b.total_amount} (incl. ${b.gst_rate}% GST)`;
        toast.success(msg);
      }

      setModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save booking');
    }
  };

  const handleCancel = async (booking) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    try {
      await cancelBooking(booking.id);
      toast.success('Booking cancelled');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  const handleHardDelete = async (booking) => {
    const label = booking.booking_number || booking.id?.slice(0, 8);
    if (!window.confirm(`Permanently delete booking ${label}? This also removes its payments, invoice, and housekeeping tasks. This cannot be undone.`)) return;
    try {
      const res = await adminDeleteBooking(booking.id);
      toast.success(`Deleted ${res.data.booking_number} (${res.data.payments_deleted} payment(s), ${res.data.invoices_deleted} invoice(s) removed)`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete booking');
    }
  };

  const handleCleanupOrphans = async () => {
    if (!window.confirm('Scan and permanently delete all orphan bookings (bookings whose guest or room no longer exists)? Their payments and invoices will also be removed. This cannot be undone.')) return;
    try {
      const res = await adminCleanupOrphans();
      const { orphan_bookings_deleted, orphan_payments_deleted, orphan_invoices_deleted } = res.data;
      if (orphan_bookings_deleted === 0 && orphan_payments_deleted === 0) {
        toast.success('No orphans found — your data is clean!');
      } else {
        toast.success(`Cleaned up ${orphan_bookings_deleted} orphan booking(s), ${orphan_payments_deleted} payment(s), ${orphan_invoices_deleted} invoice(s)`);
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cleanup failed');
    }
  };

  const handleFullReset = async () => {
    const typed = window.prompt(
      'FULL RESET — this permanently deletes ALL bookings, website reservations, guests, invoices, payments, housekeeping tasks and notifications, and frees every bed/room. Room & bed inventory is kept.\n\nThis cannot be undone. Type RESET to confirm:'
    );
    if (typed !== 'RESET') {
      if (typed !== null) toast.error('Reset cancelled — confirmation text did not match');
      return;
    }
    try {
      const res = await adminFullReset();
      const d = res.data.deleted || {};
      toast.success(`Full reset done — removed ${d.bookings || 0} bookings, ${d.site_reservations || 0} website requests, ${d.guests || 0} guests; freed ${res.data.rooms_freed || 0} rooms`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Full reset failed');
    }
  };

  const filteredBookings = bookings.filter(booking => {
    const q = searchQuery.toLowerCase();
    return (booking.guest?.name?.toLowerCase() || '').includes(q) ||
           (booking.room?.room_number?.toLowerCase() || '').includes(q) ||
           (booking.booking_number?.toLowerCase() || '').includes(q);
  });

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);

  // For edit modal: show all rooms (not just available) so the current room is selectable
  const modalRooms = editingBooking ? allRooms : rooms;

  return (
    <div data-testid="bookings-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 font-['Manrope'] tracking-tight">Bookings</h1>
          <p className="text-stone-500 mt-1">Manage reservations and booking details</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setBulkOpen(true)}
            variant="outline"
            className="border-stone-300 text-stone-700 hover:bg-stone-50 rounded-sm"
            data-testid="bulk-upload-button"
            title="Upload bookings in bulk from CSV/Excel"
          >
            <Upload className="w-4 h-4 mr-2" />Bulk Upload
          </Button>
          {isAdmin && (
            <Button
              onClick={handleCleanupOrphans}
              variant="outline"
              className="border-stone-300 text-stone-700 hover:bg-stone-50 rounded-sm"
              data-testid="cleanup-orphans-button"
              title="Delete bookings whose guest or room no longer exists"
            >
              <Sparkles className="w-4 h-4 mr-2" />Clean Orphans
            </Button>
          )}
          {isAdmin && (
            <Button
              onClick={handleFullReset}
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50 rounded-sm"
              data-testid="full-reset-button"
              title="Permanently delete all bookings, website requests, guests, invoices, payments and notifications"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />Full Reset
            </Button>
          )}
          <Button onClick={openAddModal} className="bg-sage-700 hover:bg-sage-800 text-white rounded-sm" data-testid="add-booking-button">
            <Plus className="w-4 h-4 mr-2" />New Booking
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input placeholder="Search guest, room, booking #..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 border-stone-300" data-testid="search-bookings-input" />
        </div>
        <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40 border-stone-300" data-testid="filter-booking-status">
            <Filter className="w-4 h-4 mr-2 text-stone-400" /><SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Status</SelectItem>
            {bookingStatuses.map(status => (
              <SelectItem key={status} value={status} className="capitalize">{status.replace('_', ' ')}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-stone-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-700"></div></div>
        ) : filteredBookings.length === 0 ? (
          <div className="p-12 text-center">
            <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-stone-300" />
            <p className="text-stone-500">No bookings found</p>
            <Button onClick={openAddModal} variant="outline" className="mt-4">Create your first booking</Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50 hover:bg-stone-50">
                  <TableHead className="font-semibold text-stone-700">Booking #</TableHead>
                  <TableHead className="font-semibold text-stone-700">Guest</TableHead>
                  <TableHead className="font-semibold text-stone-700">Room</TableHead>
                  <TableHead className="font-semibold text-stone-700">Check-in</TableHead>
                  <TableHead className="font-semibold text-stone-700">Check-out</TableHead>
                  <TableHead className="font-semibold text-stone-700">Total</TableHead>
                  <TableHead className="font-semibold text-stone-700">Status</TableHead>
                  <TableHead className="font-semibold text-stone-700 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBookings.map((booking, index) => (
                  <TableRow key={booking.id} className="table-row-hover animate-fade-in" style={{ animationDelay: `${index * 0.03}s` }} data-testid={`booking-row-${booking.id}`}>
                    <TableCell>
                      <span className="font-mono text-xs text-sage-700">{booking.booking_number || '-'}</span>
                    </TableCell>
                    <TableCell className="font-medium text-stone-900">
                      {booking.guest?.name || '-'}
                      {booking.group_name && (
                        <span className="block text-xs text-sage-700 font-normal">{booking.group_name}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-stone-600">{booking.room?.room_number || '-'}</TableCell>
                    <TableCell className="text-stone-600">{booking.check_in_date}</TableCell>
                    <TableCell className="text-stone-600">{booking.check_out_date}</TableCell>
                    <TableCell>
                      <span className="font-medium text-stone-900">{formatCurrency(booking.total_amount)}</span>
                      {booking.discount > 0 && (
                        <span className="block text-xs text-emerald-600">-{formatCurrency(booking.discount)} coupon</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`status-badge status-${booking.status}`}>{booking.status.replace('_', ' ')}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {booking.status === 'confirmed' && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => openEditModal(booking)}
                              className="h-8 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              data-testid={`edit-booking-${booking.id}`}>
                              <Pencil className="w-3.5 h-3.5 mr-1" />Edit
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleCancel(booking)}
                              className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`cancel-booking-${booking.id}`}>
                              <X className="w-4 h-4 mr-1" />Cancel
                            </Button>
                          </>
                        )}
                        {isAdmin && (
                          <Button variant="ghost" size="sm" onClick={() => handleHardDelete(booking)}
                            className="h-8 px-2 text-stone-500 hover:text-red-700 hover:bg-red-50"
                            title="Permanently delete (admin only)"
                            data-testid={`delete-booking-${booking.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <BookingFormDialog
        open={modalOpen}
        onOpenChange={(v) => { setModalOpen(v); if (!v) resetForm(); }}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        guests={guests}
        rooms={modalRooms}
        checkInDate={checkInDate}
        setCheckInDate={setCheckInDate}
        checkOutDate={checkOutDate}
        setCheckOutDate={setCheckOutDate}
        editingBooking={editingBooking}
      />
      <BulkUploadDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onImported={loadData}
      />
    </div>
  );
}
